import { useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  ShieldCheck,
  AlertTriangle,
  PieChart as PieIcon,
  Activity,
  Info,
  Layers,
  TrendingUp,
} from 'lucide-react';

interface PortfolioAsset {
  symbol: string;
  name: string;
  balance: number;
  value_aud: number;
  price_aud: number;
  change_24h?: number;
}

interface Props {
  portfolio: PortfolioAsset[];
  totalValue: number;
}

// Lightweight sector taxonomy. Anything missing falls through to 'Other Altcoins'.
const SECTOR_MAP: Record<string, string> = {
  // Stablecoins
  USDT: 'Stablecoin', USDC: 'Stablecoin', DAI: 'Stablecoin', TUSD: 'Stablecoin',
  USDP: 'Stablecoin', FDUSD: 'Stablecoin', PYUSD: 'Stablecoin', AUDD: 'Stablecoin',
  // Bitcoin family
  BTC: 'Bitcoin', WBTC: 'Bitcoin',
  // Ethereum family
  ETH: 'Ethereum', WETH: 'Ethereum', STETH: 'Ethereum',
  // Layer 1
  SOL: 'Layer 1', AVAX: 'Layer 1', BNB: 'Layer 1', ADA: 'Layer 1', DOT: 'Layer 1',
  TRX: 'Layer 1', NEAR: 'Layer 1', ATOM: 'Layer 1', APT: 'Layer 1', SUI: 'Layer 1',
  SEI: 'Layer 1', TIA: 'Layer 1', XRP: 'Layer 1', ALGO: 'Layer 1', FTM: 'Layer 1',
  HBAR: 'Layer 1', ICP: 'Layer 1', INJ: 'Layer 1', KAS: 'Layer 1', XLM: 'Layer 1',
  // Layer 2 / Scaling
  MATIC: 'Layer 2', POL: 'Layer 2', ARB: 'Layer 2', OP: 'Layer 2', STRK: 'Layer 2',
  IMX: 'Layer 2', MNT: 'Layer 2', MANTA: 'Layer 2',
  // DeFi
  UNI: 'DeFi', AAVE: 'DeFi', MKR: 'DeFi', LDO: 'DeFi', CRV: 'DeFi', SNX: 'DeFi',
  COMP: 'DeFi', SUSHI: 'DeFi', PENDLE: 'DeFi', JUP: 'DeFi', RAY: 'DeFi', GMX: 'DeFi',
  DYDX: 'DeFi', '1INCH': 'DeFi', BAL: 'DeFi',
  // AI
  FET: 'AI', AGIX: 'AI', RNDR: 'AI', RENDER: 'AI', TAO: 'AI', WLD: 'AI', OCEAN: 'AI',
  AKT: 'AI', NMR: 'AI', AI: 'AI', ELIZAOS: 'AI',
  // DePIN
  HNT: 'DePIN', IOTX: 'DePIN', GRT: 'DePIN', LPT: 'DePIN', AR: 'DePIN', FIL: 'DePIN',
  STORJ: 'DePIN', AIOZ: 'DePIN',
  // Gaming / Metaverse
  SAND: 'Gaming', MANA: 'Gaming', AXS: 'Gaming', GALA: 'Gaming', ENJ: 'Gaming',
  PIXEL: 'Gaming', BEAM: 'Gaming', RON: 'Gaming', PRIME: 'Gaming',
  // Meme
  DOGE: 'Meme', SHIB: 'Meme', PEPE: 'Meme', WIF: 'Meme', BONK: 'Meme', FLOKI: 'Meme',
  MEME: 'Meme', BRETT: 'Meme', POPCAT: 'Meme', MOG: 'Meme',
  // RWA
  ONDO: 'RWA', POLYX: 'RWA', MKRDAO: 'RWA',
  // Oracles / Infra
  LINK: 'Oracle', BAND: 'Oracle', PYTH: 'Oracle',
  // Privacy
  XMR: 'Privacy', ZEC: 'Privacy', DASH: 'Privacy',
  // Exchange tokens
  CRO: 'Exchange', OKB: 'Exchange', KCS: 'Exchange', GT: 'Exchange',
};

const STABLECOINS = new Set(['USDT', 'USDC', 'DAI', 'TUSD', 'USDP', 'FDUSD', 'PYUSD', 'AUDD']);
const BLUE_CHIPS = new Set(['BTC', 'ETH', 'WBTC', 'WETH', 'STETH']);

interface BandResult {
  label: string;
  color: 'good' | 'warn' | 'bad' | 'neutral';
}

const concentrationBand = (top1Pct: number): BandResult => {
  if (top1Pct >= 70) return { label: 'Extreme', color: 'bad' };
  if (top1Pct >= 50) return { label: 'High', color: 'warn' };
  if (top1Pct >= 30) return { label: 'Moderate', color: 'neutral' };
  return { label: 'Diversified', color: 'good' };
};

const stableBand = (pct: number): BandResult => {
  if (pct >= 30) return { label: 'High cash buffer', color: 'good' };
  if (pct >= 10) return { label: 'Comfortable', color: 'good' };
  if (pct >= 3) return { label: 'Low buffer', color: 'neutral' };
  return { label: 'No dry powder', color: 'warn' };
};

const blueChipBand = (pct: number): BandResult => {
  if (pct >= 50) return { label: 'Conservative', color: 'good' };
  if (pct >= 25) return { label: 'Balanced', color: 'good' };
  if (pct >= 10) return { label: 'Alt-leaning', color: 'neutral' };
  return { label: 'Speculative', color: 'warn' };
};

const positionsBand = (count: number): BandResult => {
  if (count <= 3) return { label: 'Very narrow', color: 'warn' };
  if (count <= 8) return { label: 'Focused', color: 'good' };
  if (count <= 20) return { label: 'Diversified', color: 'good' };
  return { label: 'Possibly over-spread', color: 'neutral' };
};

// Map our 'good/warn/bad/neutral' labels to design-token classes.
const colorClasses = (c: BandResult['color']) => {
  switch (c) {
    case 'good': return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30';
    case 'warn': return 'bg-amber-500/10 text-amber-400 border-amber-500/30';
    case 'bad':  return 'bg-red-500/10 text-red-400 border-red-500/30';
    default:     return 'bg-muted text-muted-foreground border-border';
  }
};

const sectorColor = (sector: string): string => {
  const palette: Record<string, string> = {
    Bitcoin: 'bg-orange-500',
    Ethereum: 'bg-indigo-500',
    Stablecoin: 'bg-emerald-500',
    'Layer 1': 'bg-blue-500',
    'Layer 2': 'bg-cyan-500',
    DeFi: 'bg-green-500',
    AI: 'bg-purple-500',
    DePIN: 'bg-fuchsia-500',
    Gaming: 'bg-pink-500',
    Meme: 'bg-yellow-500',
    RWA: 'bg-teal-500',
    Oracle: 'bg-sky-500',
    Privacy: 'bg-slate-500',
    Exchange: 'bg-rose-500',
  };
  return palette[sector] ?? 'bg-zinc-500';
};

export const PortfolioAnalysis = ({ portfolio, totalValue }: Props) => {
  const analysis = useMemo(() => {
    if (!portfolio || portfolio.length === 0 || totalValue <= 0) return null;

    const sorted = [...portfolio].sort((a, b) => b.value_aud - a.value_aud);
    const weights = sorted.map(a => ({
      symbol: a.symbol,
      name: a.name,
      value: a.value_aud,
      pct: (a.value_aud / totalValue) * 100,
    }));

    const top1Pct = weights[0]?.pct ?? 0;
    const top3Pct = weights.slice(0, 3).reduce((s, w) => s + w.pct, 0);

    // Herfindahl–Hirschman Index (sum of squared weight % values).
    // Range 0–10000. >2500 = highly concentrated.
    const hhi = weights.reduce((s, w) => s + w.pct * w.pct, 0);

    const stableValue = weights
      .filter(w => STABLECOINS.has(w.symbol.toUpperCase()))
      .reduce((s, w) => s + w.value, 0);
    const stablePct = (stableValue / totalValue) * 100;

    const blueValue = weights
      .filter(w => BLUE_CHIPS.has(w.symbol.toUpperCase()))
      .reduce((s, w) => s + w.value, 0);
    const bluePct = (blueValue / totalValue) * 100;

    // Sector buckets
    const sectorTotals: Record<string, number> = {};
    for (const w of weights) {
      const sector = SECTOR_MAP[w.symbol.toUpperCase()] ?? 'Other Altcoins';
      sectorTotals[sector] = (sectorTotals[sector] ?? 0) + w.value;
    }
    const sectors = Object.entries(sectorTotals)
      .map(([sector, value]) => ({ sector, value, pct: (value / totalValue) * 100 }))
      .sort((a, b) => b.value - a.value);

    // Composite health score 0–100. Lower is riskier; higher is better balanced.
    // Inputs each contribute up to a max — penalties for: high concentration,
    // no stable buffer, no blue chips, very few positions.
    let score = 100;
    if (top1Pct > 50) score -= Math.min(30, (top1Pct - 50) * 0.8);
    else if (top1Pct > 30) score -= (top1Pct - 30) * 0.4;
    if (stablePct < 5) score -= 8;
    else if (stablePct < 10) score -= 4;
    if (bluePct < 25) score -= 10;
    else if (bluePct < 40) score -= 4;
    if (weights.length < 4) score -= 8;
    if (hhi > 4000) score -= 10;
    else if (hhi > 2500) score -= 5;
    score = Math.max(0, Math.min(100, Math.round(score)));

    // Plain-English educational observations
    const observations: string[] = [];
    if (top1Pct >= 50) {
      observations.push(
        `${weights[0].symbol} is ${top1Pct.toFixed(0)}% of your portfolio. Concentrated portfolios historically experience larger drawdowns when that single asset moves.`
      );
    }
    if (stablePct < 5 && weights.length > 1) {
      observations.push(
        'You have very little in stablecoins. Holding some stables can give you flexibility to buy dips without selling other assets.'
      );
    }
    if (bluePct < 25) {
      observations.push(
        `BTC + ETH make up only ${bluePct.toFixed(0)}% of your bag. Most altcoins historically have correlation > 0.8 to BTC, so an alt-heavy portfolio is effectively a leveraged BTC bet with extra idiosyncratic risk.`
      );
    }
    const memePct = sectors.find(s => s.sector === 'Meme')?.pct ?? 0;
    if (memePct >= 20) {
      observations.push(
        `Meme tokens are ${memePct.toFixed(0)}% of your portfolio. Memes historically have 60–90% drawdowns and limited fundamental floor.`
      );
    }
    const otherAlt = sectors.find(s => s.sector === 'Other Altcoins')?.pct ?? 0;
    if (otherAlt >= 30) {
      observations.push(
        `${otherAlt.toFixed(0)}% of your value is in tokens we couldn't classify. Consider reviewing them in the AI Coin Reviewer.`
      );
    }
    if (weights.length < 3) {
      observations.push(
        'A portfolio with fewer than 3 positions has very high single-token risk. Most studies show diminishing returns to diversification past 8–12 positions.'
      );
    }

    return {
      weights,
      top1Pct,
      top3Pct,
      hhi,
      stablePct,
      bluePct,
      sectors,
      score,
      observations,
    };
  }, [portfolio, totalValue]);

  if (!analysis) return null;

  const concBand = concentrationBand(analysis.top1Pct);
  const stableBandRes = stableBand(analysis.stablePct);
  const blueBandRes = blueChipBand(analysis.bluePct);
  const posBandRes = positionsBand(analysis.weights.length);

  const scoreColor =
    analysis.score >= 75 ? 'text-emerald-400' :
    analysis.score >= 55 ? 'text-amber-400' :
    'text-red-400';

  const scoreBar =
    analysis.score >= 75 ? 'bg-emerald-500' :
    analysis.score >= 55 ? 'bg-amber-500' :
    'bg-red-500';

  return (
    <Card className="gradient-card border-border/50">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" />
              Portfolio Analysis
            </CardTitle>
            <CardDescription>
              Deterministic risk &amp; allocation snapshot — refreshes whenever your holdings sync.
            </CardDescription>
          </div>
          <Badge variant="outline" className="text-xs">
            <Info className="h-3 w-3 mr-1" />
            Educational, not financial advice
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Health Scorecard */}
        <div className="rounded-lg border border-border/60 bg-muted/20 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <Activity className="h-4 w-4 text-primary" />
              Portfolio Health Score
            </div>
            <div className={`text-3xl font-bold ${scoreColor}`}>{analysis.score}<span className="text-sm text-muted-foreground">/100</span></div>
          </div>
          <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
            <div
              className={`h-full ${scoreBar} transition-all`}
              style={{ width: `${analysis.score}%` }}
            />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
            <Metric
              label="Top holding"
              value={`${analysis.top1Pct.toFixed(0)}%`}
              sub={analysis.weights[0]?.symbol}
              band={concBand}
            />
            <Metric
              label="Stablecoin buffer"
              value={`${analysis.stablePct.toFixed(0)}%`}
              band={stableBandRes}
            />
            <Metric
              label="BTC + ETH"
              value={`${analysis.bluePct.toFixed(0)}%`}
              band={blueBandRes}
            />
            <Metric
              label="Positions"
              value={`${analysis.weights.length}`}
              band={posBandRes}
            />
          </div>
        </div>

        {/* Concentration */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <AlertTriangle className="h-4 w-4 text-primary" />
              Concentration Risk
            </div>
            <Badge variant="outline" className={`text-xs ${colorClasses(concBand.color)}`}>
              {concBand.label}
            </Badge>
          </div>
          <div className="grid grid-cols-3 gap-3 text-xs">
            <Stat label="Top 1" value={`${analysis.top1Pct.toFixed(1)}%`} />
            <Stat label="Top 3 combined" value={`${analysis.top3Pct.toFixed(1)}%`} />
            <Stat
              label="HHI"
              value={analysis.hhi.toFixed(0)}
              hint={analysis.hhi > 2500 ? 'Highly concentrated' : analysis.hhi > 1500 ? 'Moderate' : 'Diversified'}
            />
          </div>
        </div>

        {/* Sector Exposure */}
        <div>
          <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-foreground">
            <PieIcon className="h-4 w-4 text-primary" />
            Sector Exposure
          </div>

          {/* Stacked bar */}
          <div className="flex w-full h-3 rounded-full overflow-hidden border border-border/50 mb-3">
            {analysis.sectors.map(s => (
              <div
                key={s.sector}
                className={sectorColor(s.sector)}
                style={{ width: `${s.pct}%` }}
                title={`${s.sector} ${s.pct.toFixed(1)}%`}
              />
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {analysis.sectors.map(s => (
              <div key={s.sector} className="flex items-center gap-2 text-xs">
                <span className={`inline-block w-2.5 h-2.5 rounded-sm ${sectorColor(s.sector)}`} />
                <span className="text-foreground font-medium flex-1">{s.sector}</span>
                <span className="text-muted-foreground tabular-nums">{s.pct.toFixed(1)}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* Top weights */}
        <div>
          <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-foreground">
            <Layers className="h-4 w-4 text-primary" />
            Position Weights
          </div>
          <div className="space-y-2">
            {analysis.weights.slice(0, 8).map(w => (
              <div key={w.symbol} className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-semibold text-foreground">{w.symbol}</span>
                  <span className="text-muted-foreground tabular-nums">{w.pct.toFixed(1)}%</span>
                </div>
                <Progress value={Math.min(100, w.pct)} className="h-1.5" />
              </div>
            ))}
          </div>
        </div>

        {/* Educational observations */}
        {analysis.observations.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-foreground">
              <TrendingUp className="h-4 w-4 text-primary" />
              Educational Observations
            </div>
            <ul className="space-y-2">
              {analysis.observations.map((obs, i) => (
                <li
                  key={i}
                  className="text-xs text-muted-foreground bg-muted/30 border border-border/50 rounded-md p-2 leading-relaxed"
                >
                  {obs}
                </li>
              ))}
            </ul>
          </div>
        )}

        <Alert className="border-border/50">
          <Info className="h-4 w-4" />
          <AlertDescription className="text-xs">
            This analysis is general information only and not personal financial advice. It does not consider your
            objectives, financial situation, or needs. Crypto is high-risk — consider seeking advice from a licensed
            AFSL holder before making investment decisions.
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );
};

const Metric = ({
  label,
  value,
  sub,
  band,
}: {
  label: string;
  value: string;
  sub?: string;
  band: BandResult;
}) => (
  <div className="rounded-md border border-border/50 bg-background/30 p-2">
    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className="text-base font-bold text-foreground tabular-nums">{value}</div>
    {sub && <div className="text-[10px] text-muted-foreground truncate">{sub}</div>}
    <Badge variant="outline" className={`mt-1 text-[10px] ${colorClasses(band.color)}`}>
      {band.label}
    </Badge>
  </div>
);

const Stat = ({ label, value, hint }: { label: string; value: string; hint?: string }) => (
  <div className="rounded-md border border-border/50 bg-background/30 p-2">
    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className="text-base font-bold text-foreground tabular-nums">{value}</div>
    {hint && <div className="text-[10px] text-muted-foreground">{hint}</div>}
  </div>
);

export default PortfolioAnalysis;
